import '@deque/cauldron-react/lib/cauldron.css';
import '@deque/cauldron-styles/dist/index.css';
import type { FeatureFlagId } from '@deque/feature-flags';
import React, { type ReactElement } from 'react';
import { Route, BrowserRouter as Router, Switch } from 'react-router-dom';
import 'typeface-roboto/index.css';

import analyticsInstances from '~/common/analyticsInstances';
import AxeProductTrial from '~/common/components/AxeProductTrial';
import FeatureRequired from '~/common/components/FeatureRequired';
import Loadable from '~/common/components/Loadable';
import NotOnPrem from '~/common/components/NotOnPrem';
import Protected from '~/common/components/Protected';
import type { SupportedTrialProductSlugs } from '~/common/constants';
import { PRODUCT_URLS, ProductSlugs } from '~/common/constants';
import { AnalyticsProvider } from '~/common/contexts/analytics';
import { AuthProvider } from '~/common/contexts/auth';
import { EnterprisesProvider } from '~/common/contexts/enterprises';
import { Provider as FeatureFlagProvider } from '~/common/contexts/featureFlags';
import { GlobalToastProvider } from '~/common/contexts/globalToast';
import { Provider as OnPremProvider } from '~/common/contexts/onPrem';
import { ProductsProvider } from '~/common/contexts/products';
import { Provider as ServerInfoProvider } from '~/common/contexts/serverInfo';

import AppLayout from './components/AppLayout';
import AppRoutes from './components/AppRoutes';
import Maintenance from './components/Maintenance';
import StripeElements from './components/StripeElements';
import SupportInfoDrawer from './components/SupportInfoDrawer/SupportInfoDrawer';

import { ConfigurationProvider } from '~/common/contexts/Configuration';
import { UserActivityProvider } from '~/common/contexts/userActivity';
import Issue from './pages/Issue';
import IssueV2 from './pages/IssueV2';
import JiraDirectConnected from './pages/JiraDirectConnected';
import JiraOnPremConnected from './pages/JiraOnPremConnected';
import TestError from './pages/TestError';

const AxeDevTools = React.lazy(
  () =>
    import(/* webpackChunkName: "AxeDevTools" */ '../axe-devtools/AxeDevTools')
);

const AxeDevToolsMobile = React.lazy(
  () =>
    import(
      /* webpackChunkName: "AxeDevToolsMobile" */ '../axe-devtools-mobile/AxeDevToolsMobile'
    )
);

const AxeWatcher = React.lazy(
  () => import(/* webpackChunkName: "AxeWatcher" */ '../axe-watcher/AxeWatcher')
);

const AxeAuditor = React.lazy(
  () => import(/* webpackChunkName: "AxeAuditor" */ '../axe-auditor/AxeAuditor')
);

const AxeSpider = React.lazy(
  () => import(/* webpackChunkName: "AxeSpider" */ '../axe-spider/AxeSpider')
);

const createProductTrialPage = (
  productSlug: SupportedTrialProductSlugs,
  featureFlagId?: FeatureFlagId
): React.FC => {
  const ProductTrialPage: React.FC = () => (
    <NotOnPrem>
      <Protected>
        {featureFlagId ? (
          <FeatureRequired featureFlagId={featureFlagId}>
            <AxeProductTrial productSlug={productSlug} />
          </FeatureRequired>
        ) : (
          <AxeProductTrial productSlug={productSlug} />
        )}
      </Protected>
    </NotOnPrem>
  );

  return ProductTrialPage;
};

interface AppProps {
  isOnPrem: boolean;
  stripePublishableKey?: string;
}

const App = ({ isOnPrem, stripePublishableKey }: AppProps): ReactElement => (
  <GlobalToastProvider>
    <Maintenance>
      <OnPremProvider isOnPrem={isOnPrem}>
        <AuthProvider isOnPrem={isOnPrem}>
          <ServerInfoProvider>
            <AnalyticsProvider analytics={analyticsInstances}>
              <FeatureFlagProvider>
                <EnterprisesProvider>
                  <ProductsProvider>
                    <ConfigurationProvider>
                      <StripeElements
                        stripePublishableKey={stripePublishableKey}
                      >
                        <Router>
                          <>
                            <SupportInfoDrawer />
                            <Switch>
                              <Route
                                exact
                                path="/test-error"
                                component={TestError}
                              />
                              <Route
                                exact
                                path={
                                  PRODUCT_URLS[ProductSlugs.axeLinter].trial
                                }
                                component={createProductTrialPage(
                                  ProductSlugs.axeLinter,
                                  'axe_linter_online_try'
                                )}
                              />
                              <Route
                                exact
                                path={
                                  PRODUCT_URLS[
                                    ProductSlugs.axeDevToolsExtension
                                  ].trial
                                }
                                component={createProductTrialPage(
                                  ProductSlugs.axeDevToolsExtension
                                )}
                              />
                              <Route
                                exact
                                path={
                                  PRODUCT_URLS[ProductSlugs.axeSpider].trial
                                }
                                component={createProductTrialPage(
                                  ProductSlugs.axeSpider,
                                  'axe_spider_v1'
                                )}
                              />
                              <Route
                                path={
                                  PRODUCT_URLS[
                                    ProductSlugs.axeDevToolsExtension
                                  ].root
                                }
                              >
                                <Loadable>
                                  <UserActivityProvider
                                    productSlug={
                                      ProductSlugs.axeDevToolsExtension
                                    }
                                  >
                                    <AxeDevTools />
                                  </UserActivityProvider>
                                </Loadable>
                              </Route>
                              <Route
                                path={
                                  PRODUCT_URLS[ProductSlugs.axeDevToolsWatcher]
                                    .root
                                }
                              >
                                <FeatureRequired featureFlagId="axe_devtools_watcher_v1">
                                  <Loadable>
                                    <UserActivityProvider
                                      productSlug={
                                        ProductSlugs.axeDevToolsWatcher
                                      }
                                    >
                                      <AxeWatcher />
                                    </UserActivityProvider>
                                  </Loadable>
                                </FeatureRequired>
                              </Route>
                              <Route
                                path={
                                  PRODUCT_URLS[ProductSlugs.axeDevToolsMobile]
                                    .root
                                }
                              >
                                <NotOnPrem>
                                  <FeatureRequired featureFlagId="mobile_free_trial">
                                    <Loadable>
                                      <UserActivityProvider
                                        productSlug={
                                          ProductSlugs.axeDevToolsMobile
                                        }
                                      >
                                        <AxeDevToolsMobile />
                                      </UserActivityProvider>
                                    </Loadable>
                                  </FeatureRequired>
                                </NotOnPrem>
                              </Route>
                              <Route
                                path={
                                  PRODUCT_URLS[ProductSlugs.axeAuditor].root
                                }
                              >
                                <NotOnPrem>
                                  <FeatureRequired featureFlagId="axe_auditor_v3">
                                    <Loadable>
                                      <UserActivityProvider
                                        productSlug={ProductSlugs.axeAuditor}
                                      >
                                        <AxeAuditor />
                                      </UserActivityProvider>
                                    </Loadable>
                                  </FeatureRequired>
                                </NotOnPrem>
                              </Route>
                              <Route
                                path={PRODUCT_URLS[ProductSlugs.axeSpider].root}
                              >
                                <FeatureRequired featureFlagId="axe_spider_v1">
                                  <Loadable>
                                    <UserActivityProvider
                                      productSlug={ProductSlugs.axeSpider}
                                    >
                                      <AxeSpider />
                                    </UserActivityProvider>
                                  </Loadable>
                                </FeatureRequired>
                              </Route>
                              <Route exact path="/issues/:id">
                                <FeatureRequired
                                  featureFlagId="launchpad_devtools_issues_ui_v1"
                                  off={
                                    <AppLayout>
                                      <Issue />
                                    </AppLayout>
                                  }
                                >
                                  <UserActivityProvider
                                    productSlug={
                                      ProductSlugs.axeDevToolsExtension
                                    }
                                  >
                                    <IssueV2 />
                                  </UserActivityProvider>
                                </FeatureRequired>
                              </Route>
                              <Route exact path="/jira-onprem/connected">
                                <Protected>
                                  <JiraOnPremConnected />
                                </Protected>
                              </Route>
                              <Route exact path="/jira-direct/connected">
                                <Protected>
                                  <JiraDirectConnected />
                                </Protected>
                              </Route>
                              <Route>
                                <AppLayout>
                                  <UserActivityProvider
                                    productSlug={'axe-account'}
                                  >
                                    <AppRoutes />
                                  </UserActivityProvider>
                                </AppLayout>
                              </Route>
                            </Switch>
                          </>
                        </Router>
                      </StripeElements>
                    </ConfigurationProvider>
                  </ProductsProvider>
                </EnterprisesProvider>
              </FeatureFlagProvider>
            </AnalyticsProvider>
          </ServerInfoProvider>
        </AuthProvider>
      </OnPremProvider>
    </Maintenance>
  </GlobalToastProvider>
);

export default App;
